import type {JSX} from 'react'
import React, {useState, useEffect, useCallback} from 'react'
import type {Libraries} from '@react-google-maps/api'
import {useJsApiLoader, MarkerF} from '@react-google-maps/api'
import {requireHexCode} from '@/products/flexible-web-toolkit/colors'
import {Map} from '@/products/flexible-web-toolkit/blocks/office-location/map'
import {LoadingOverlay} from '@/products/disease-map/loading-overlay'
import {OfficeDetails} from '@/products/flexible-web-toolkit/components/office-details'
import {Dropdown} from '@/products/flexible-web-toolkit/components/dropdown'

const libraries: Libraries = ['geometry', 'places']
// fitBounds treats a single point as needing max zoom, so use a fixed zoom instead
const SINGLE_MARKER_ZOOM = 18
interface MarkerProps {
	country: string
	key: number
	lat: number
	lng: number
	title: string
	description: string
	marker_description: string
	primaryContactNumber?: string
	secondaryContactNumber?: string
	emailAddress?: string
	ctaTitle?: string
	primaryCta?: {
		url: string
		text: string
		isExternal: boolean
	}
	secondaryCta?: {
		url: string
		text: string
		isExternal: boolean
	}
}
interface WorldMapProps {
	apiKey?: string
	title?: string
	description?: string
	markers: MarkerProps[]
	showCountryFilter?: boolean
	countryDropdownLabel?: string
	countryDropdownPlaceholder?: string
}

export const WorldMap = ({
	apiKey,
	title = '',
	description = '',
	markers,
	showCountryFilter = false,
	countryDropdownLabel = 'Filter by Countries',
	countryDropdownPlaceholder = 'Please Select a Country',
}: WorldMapProps): JSX.Element => {
	// pre-select the first marker's country so the dropdown and map match the default active marker
	const initialCountry = showCountryFilter ? (markers[0]?.country ?? '') : ''
	const initialMarkers = initialCountry
		? markers.filter((marker) => marker.country === initialCountry)
		: markers

	const [map, setMap] = useState<google.maps.Map | null>(null)
	const [activeMarkers, setActiveMarkers] = useState(initialMarkers)
	const [activeMarker, setActiveMarker] = useState(initialMarkers[0])
	const [activeCountry, setActiveCountry] = useState(initialCountry)

	const dropdownOptions: {
		text: string
		value: string
	}[] = []

	if (showCountryFilter) {
		markers.map((marker) => {
			if (
				dropdownOptions.find((x) => x.value === marker.country) ===
				undefined
			) {
				dropdownOptions.push({
					text: marker.country,
					value: marker.country,
				})
			}
			return marker
		})
	}

	const {isLoaded} = useJsApiLoader({
		googleMapsApiKey: apiKey ?? '',
		libraries,
	})

	const onDropdownChange = (
		countryValue: React.SetStateAction<string> | undefined
	): void => {
		const filteredMarkers = markers.filter((x) => {
			if (countryValue === '') {
				return true
			}
			return x.country === countryValue
		})
		setActiveCountry(countryValue ?? '')
		setActiveMarkers(filteredMarkers)
		if (filteredMarkers.length) setActiveMarker(filteredMarkers[0])
	}

	const zoomToMarkers = useCallback((): void => {
		if (activeMarkers.length === 0) return

		const [marker] = activeMarkers
		if (activeMarkers.length === 1 && marker) {
			map?.setCenter({lat: marker.lat, lng: marker.lng})
			map?.setZoom(SINGLE_MARKER_ZOOM)
			return
		}

		const bounds = new window.google.maps.LatLngBounds()

		activeMarkers.forEach((m) => {
			bounds.extend(new google.maps.LatLng(m.lat, m.lng))
		})

		map?.setCenter(bounds.getCenter())
		map?.panToBounds(bounds)
		map?.fitBounds(bounds)
	}, [activeMarkers, map])

	useEffect(() => {
		if (markers.length < 1 || !map) return

		zoomToMarkers()
	}, [activeMarkers, map, markers.length, zoomToMarkers])

	const onMapLoad = useCallback((mapInstance: google.maps.Map | null) => {
		mapInstance?.setOptions({minZoom: 3, maxZoom: 0})

		setMap(mapInstance)
	}, [])

	const getMarkerSvg = (marker: MarkerProps): string => {
		const isActive = marker.key === activeMarker?.key
		const colour = isActive
			? requireHexCode('elanco-teal')
			: requireHexCode('elanco-dark-navy')
		return window.btoa(`
<svg viewBox="117.443 80.313 246.107 310.581" xmlns="http://www.w3.org/2000/svg">
  <ellipse style="paint-order: fill; fill: rgb(128, 146, 158); stroke: rgb(77, 102, 120); stroke-width: 2px;" cx="324.09" cy="351.434" rx="39.46" ry="39.46"/>
  <rect style="stroke-linecap: round; stroke-miterlimit: 1; stroke-linejoin: round; stroke: rgb(0, 114, 206); fill-rule: nonzero; stroke-width: 0px; paint-order: stroke; fill: ${colour};" x="117.443" y="80.313" width="208.456" height="197.848" rx="25" ry="25"/>
  <path d="M 254.272 201.002 L 225.244 228.338 C 223.382 230.09 220.48 230.09 218.624 228.338 L 189.597 201.002 C 171.69 183.524 171.69 154.74 189.597 137.263 C 207.578 119.7 236.284 119.7 254.272 137.263 C 272.172 154.74 272.172 183.524 254.272 201.002 Z M 247.505 144.12 C 233.279 130.25 210.584 130.25 196.363 144.12 C 182.297 157.811 182.266 180.396 196.293 194.131 L 221.934 218.28 L 247.536 194.171 C 261.582 180.441 261.57 157.83 247.505 144.12 Z M 221.934 188.857 C 207.157 188.799 197.993 172.768 205.427 160.003 C 212.866 147.237 231.332 147.314 238.67 160.137 C 240.33 163.041 241.203 166.323 241.203 169.666 C 241.176 180.286 232.554 188.876 221.934 188.857 Z M 221.934 179.258 C 229.324 179.225 233.905 171.211 230.188 164.824 C 226.466 158.444 217.228 158.477 213.565 164.895 C 212.731 166.343 212.297 167.986 212.297 169.66 C 212.303 174.973 216.62 179.271 221.934 179.258 Z" style="stroke-miterlimit: 3;" fill="#fff"/>
  <path d="M 303.638 472.247 L 352.868 654.342 L 254.407 654.342 L 303.638 472.247 Z" style="stroke: rgb(0, 0, 0); stroke-width: 0px; fill: ${colour};" transform="matrix(-0.006135, -0.999981, 1.002915, 0.472139, -328.125977, 297.440918)"/>
</svg>`)
	}

	return (
		<div>
			{title || description || showCountryFilter ? (
				<div className="content -mb-8 py-4 md:my-8">
					{title || description ? (
						<div className="pb-6 text-center">
							{title ? <h2>{title}</h2> : null}
							{description ? <p>{description}</p> : null}
						</div>
					) : null}

					{showCountryFilter ? (
						<Dropdown
							className="w-full md:mx-auto md:w-1/2 lg:w-1/3"
							dropdownData={[
								{
									text: countryDropdownPlaceholder,
									value: '',
								},
								...dropdownOptions,
							]}
							fieldName="country"
							label={countryDropdownLabel}
							updateFilterValue={onDropdownChange}
							value={activeCountry}
						/>
					) : null}
				</div>
			) : null}
			<div className="mx-4 overflow-hidden rounded-lg shadow-primary md:mx-8 md:flex">
				<OfficeDetails
					className="px-4 py-8 md:m-0 md:w-2/5 lg:w-[30%]"
					ctaTitle={activeMarker?.ctaTitle}
					description={activeMarker?.description}
					emailAddress={activeMarker?.emailAddress}
					markerDescription={activeMarker?.marker_description}
					primaryContactNumber={activeMarker?.primaryContactNumber}
					primaryCta={activeMarker?.primaryCta}
					secondaryContactNumber={
						activeMarker?.secondaryContactNumber
					}
					secondaryCta={activeMarker?.secondaryCta}
					title={activeMarker?.title}
				/>
				<div className="mx-auto h-[300px] w-full overflow-hidden shadow-primary md:mx-0 md:mb-0 md:h-[700px] md:w-3/5 md:rounded-none md:shadow-none lg:w-7/10">
					{!isLoaded && <LoadingOverlay color="#fff" />}
					{isLoaded ? (
						<Map
							containerClassName="w-full h-full"
							defaultLat={0}
							defaultLng={0}
							initialZoom={SINGLE_MARKER_ZOOM}
							maxZoom={20}
							minZoom={3}
							onLoad={onMapLoad}
						>
							{activeMarkers.map((marker) => (
								<MarkerF
									key={marker.key}
									onClick={() => {
										setActiveMarker(marker)
									}}
									options={{
										icon: {
											url: `data:image/svg+xml;base64,${getMarkerSvg(
												marker
											)}`,
											scaledSize:
												new window.google.maps.Size(
													75,
													75
												),
											labelOrigin:
												new window.google.maps.Point(
													25,
													16
												),
										},
									}}
									position={{
										lat: marker.lat,
										lng: marker.lng,
									}}
								/>
							))}
						</Map>
					) : null}
				</div>
			</div>
		</div>
	)
}
